import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {fetchWithAuth} from '../../libs/apiUtils'
import Modal from '../Modal'
import { StarIcon } from '@heroicons/react/24/outline'

interface EditGroupModalProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  group: { id?: string; name?: string; isDefault?: boolean }
  onSuccess: () => void
  isSuperAdmin?: boolean
}

export default function EditGroupModal({ 
  isOpen, 
  setIsOpen, 
  group, 
  onSuccess,
  isSuperAdmin = false,
}: EditGroupModalProps) {
  const [name, setName] = useState(group.name || '')
  const [isDefault, setIsDefault] = useState(group.isDefault ?? false)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return toast.error('Nama grup tidak boleh kosong')
    if (!group.id) return toast.error('Tidak ada grup yang dipilih')

    try {
      setLoading(true)
      const body: any = { id: group.id, name }
      if (isSuperAdmin) body.isDefault = isDefault

      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/group`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || data[0]?.message || 'Error saat memperbarui grup')
        return
      }

      toast.success('Grup berhasil diperbarui')
      setIsOpen(false)
      onSuccess()
    } catch (err) {
      toast.error('Kesalahan jaringan, periksa koneksi Anda')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (group.name) setName(group.name)
    setIsDefault(group.isDefault ?? false)
  }, [group])

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <h3 className="text-xl font-bold text-neutral-800 mb-4">Edit Grup</h3>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="edit-group-name" className="block text-sm font-semibold text-neutral-600 mb-2">
            Nama Grup
          </label>
          <input
            type="text"
            id="edit-group-name"
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>

        {/* isDefault toggle — only visible to super_admin, for promoting/demoting a group */}
        {isSuperAdmin && (
          <div className="mb-6 p-3 bg-amber-50 border border-amber-100 rounded-lg">
            <label className="flex items-center gap-3 cursor-pointer">
              <div className="relative shrink-0">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
                <div className={`w-10 h-6 rounded-full transition-colors ${isDefault ? 'bg-amber-500' : 'bg-neutral-300'}`}></div>
                <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${isDefault ? 'translate-x-4' : ''}`}></div>
              </div>
              <div>
                <span className="text-sm font-semibold text-neutral-700 flex items-center gap-1">
                  <StarIcon className="size-4 text-amber-500" /> Grup Bawaan
                </span>
                <p className="text-xs text-neutral-500 mt-0.5">Semua admin dapat melihat grup ini; kategorinya bersifat read-only untuk mereka.</p>
              </div>
            </label>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 text-neutral-600 font-medium rounded-lg hover:bg-neutral-100 transition-colors"
            onClick={() => setIsOpen(false)}
            disabled={loading}
          >
            Batal
          </button>
          <button
            type="submit"
            className="bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700 active:scale-95 disabled:opacity-50 disabled:active:scale-100 transition-all"
            disabled={loading}
          >
            {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
